import { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardColors,
  Text,
  Button,
  Select,
  Separator,
  selectValsToDropdownOptions,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons'
import EditImportantDatesModal, {
  getMeridianForDateTime,
} from './EditImportantDatesModal'
import {
  formatDateForUpsellImportantDates,
  formatTimeForUpsellImportantDates,
  SCorpSetUpStatus,
  RequestSource,
  formatRequestSourceLabel,
  formatSetUpStatusLabelForSetUpStatus,
} from '@/features/user/dashboard/s-corp-upsell/utils'
import {
  fetchAdminSCorpElectionStatus,
  postSCorpElectionStatus,
  putSCorpElectionStatus,
} from '@/features/user/dashboard/s-corp-upsell/sCorpActions'
import { useReselector } from '@/utils/sharedHooks'
import { selectSCorpSetUpStatusesForUser } from '@/features/user/dashboard/s-corp-upsell/sCorpActions.selector'
import { useAppDispatch } from '@/utils/typeHelpers'

const makeAdditionalParamsForImportantDates = (status: SCorpSetUpStatus) => {
  switch (status) {
    case SCorpSetUpStatus.request_submitted:
      return {
        initialRequestSubmittedAt: new Date().toISOString(),
      }
    case SCorpSetUpStatus.form_2553_filed:
      return {
        form2553FiledAt: new Date().toISOString(),
      }
    case SCorpSetUpStatus.cp261_received:
      return {
        cp261UploadedAt: new Date().toISOString(),
      }
    case SCorpSetUpStatus.withdrew_election:
    case SCorpSetUpStatus.disqualified_automatically:
    case SCorpSetUpStatus.disqualified_manually:
    case SCorpSetUpStatus.blocked_by_entity_ein:
    case SCorpSetUpStatus.blocked_by_signature:
    case SCorpSetUpStatus.qualification_pending:
    case SCorpSetUpStatus.drafting_form_2553:
    case SCorpSetUpStatus.not_interested:
      return {}
    default:
      return status satisfies never
  }
}

export const SCorpPanel = ({ userId }: { userId: number }) => {
  const dispatch = useAppDispatch()

  const [editDatesModalOpen, setEditDatesModalOpen] = useState(false)

  const electionStatus = useReselector(selectSCorpSetUpStatusesForUser, userId)

  useEffect(() => {
    dispatch(fetchAdminSCorpElectionStatus(userId, true))
  }, [dispatch, userId])

  const formattedImportantDates = useMemo(() => {
    if (!electionStatus) {
      return []
    }
    return [
      {
        label: 'Request Submitted On:',
        date: electionStatus.initialRequestSubmittedAt
          ? formatDateForUpsellImportantDates(
              electionStatus.initialRequestSubmittedAt
            )
          : '-',
        time: electionStatus.initialRequestSubmittedAt
          ? formatTimeForUpsellImportantDates(
              electionStatus.initialRequestSubmittedAt
            )
          : '-',
        meridian: electionStatus.initialRequestSubmittedAt
          ? getMeridianForDateTime(electionStatus.initialRequestSubmittedAt)
          : '-',
      },
      {
        label: 'Form 2553 Filed On:',
        date: electionStatus.form2553FiledAt
          ? formatDateForUpsellImportantDates(electionStatus.form2553FiledAt)
          : '-',
        time: electionStatus.form2553FiledAt
          ? formatTimeForUpsellImportantDates(electionStatus.form2553FiledAt)
          : '-',
        meridian: electionStatus.form2553FiledAt
          ? getMeridianForDateTime(electionStatus.form2553FiledAt)
          : '-',
      },
      {
        label: 'CP261 Received On:',
        date: electionStatus.cp261UploadedAt
          ? formatDateForUpsellImportantDates(electionStatus.cp261UploadedAt)
          : '-',
        time: electionStatus.cp261UploadedAt
          ? formatTimeForUpsellImportantDates(electionStatus.cp261UploadedAt)
          : '-',
        meridian: electionStatus.cp261UploadedAt
          ? getMeridianForDateTime(electionStatus.cp261UploadedAt)
          : '-',
      },
    ]
  }, [electionStatus])

  const handleRequestSourceDropdownChange = async (source: RequestSource) => {
    if (!electionStatus) {
      await postSCorpElectionStatus({
        userId,
        status: SCorpSetUpStatus.request_submitted,
        initialRequestSource: source,
      })(dispatch)
    } else {
      await putSCorpElectionStatus({
        id: electionStatus.id,
        payload: {
          initialRequestSource: source,
        },
      })(dispatch)
    }
  }

  const handleSetUpStatusDropdownChange = async (status: SCorpSetUpStatus) => {
    if (!electionStatus) {
      return
    }

    await putSCorpElectionStatus({
      id: electionStatus.id,
      payload: {
        status,
        ...makeAdditionalParamsForImportantDates(status),
      },
    })(dispatch)
  }

  return (
    <Card color={CardColors.LEVEL_0} className="w-full">
      <div className="space-y-4">
        <Text variant={TypographyStyles.H1}>S Corporation Election</Text>

        <div className="w-full md:w-2/3 pb-1.5">
          <Text variant={TypographyStyles.EYEBROW}>Request Source</Text>
          <Select
            options={selectValsToDropdownOptions(
              Object.values(RequestSource),
              formatRequestSourceLabel
            ).map((opt) => ({
              ...opt,
              value: opt.value as RequestSource,
            }))}
            placeholder={
              electionStatus?.initialRequestSource
                ? formatRequestSourceLabel(electionStatus.initialRequestSource)
                : 'No request made'
            }
            defaultValue={
              electionStatus?.initialRequestSource
                ? formatRequestSourceLabel(electionStatus.initialRequestSource)
                : 'No request made'
            }
            onValueChange={handleRequestSourceDropdownChange}
          />
        </div>

        {electionStatus && (
          <>
            <div className="w-full md:w-2/3 pb-1.5">
              <Text variant={TypographyStyles.EYEBROW}>Set Up Status</Text>
              <Select
                options={selectValsToDropdownOptions(
                  Object.values(SCorpSetUpStatus),
                  formatSetUpStatusLabelForSetUpStatus
                ).map((opt) => ({
                  ...opt,
                  value: opt.value as SCorpSetUpStatus,
                }))}
                placeholder={formatSetUpStatusLabelForSetUpStatus(
                  electionStatus.status
                )}
                value={electionStatus.status}
                onValueChange={handleSetUpStatusDropdownChange}
              />
            </div>
            <Separator />

            <div className="flex flex-wrap items-center justify-between">
              <Text variant={TypographyStyles.EYEBROW}>Important Dates</Text>
              <Button
                variant="secondary"
                iconLeft={faPenToSquare}
                onClick={() => setEditDatesModalOpen(true)}
                size="sm"
                className="flex items-center space-x-1"
              >
                Edit
              </Button>
            </div>
            <Separator />
            {formattedImportantDates.map((date) => (
              <div
                key={date.label}
                className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center border-b border-neutral-100 pb-1"
              >
                <Text variant={TypographyStyles.BODY_SM}>{date.label}</Text>
                <Text variant={TypographyStyles.BODY_SM}>{date.date}</Text>
                <Text variant={TypographyStyles.BODY_SM}>
                  {date.time} {date.meridian}
                </Text>
              </div>
            ))}

            <EditImportantDatesModal
              open={editDatesModalOpen}
              onClose={() => setEditDatesModalOpen(false)}
              electionStatus={electionStatus}
            />
          </>
        )}
      </div>
    </Card>
  )
}
